import * as React from 'react';
import cx from 'classnames/bind';
import onToastClicked from '../utils/onToastClicked';
import styles from './Toast.scss';
import type ToastItem from '../store/schema/ToastItem';
import toastViewState from '../store/Store';
import { addAriaLiveLabel, removeAriaLiveLabel } from 'mini-accessibility';
import { FluentSvgIcon } from 'mini-fluent-svg-icons/lib/components/FluentSvgIcon';
import { observer } from 'mobx-react-lite';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';

const classNames = cx.bind(styles);

export interface ToastProps extends React.HTMLProps<HTMLAnchorElement> {
    item: ToastItem;
    onRetry: (remainingRetries: number) => void;
    // This indicates to focus on the toast button when the toast is shown
    shouldFocusOnToastButton?: boolean;
}

const Toast = (props: ToastProps) => {
    const { item, onRetry, shouldFocusOnToastButton } = props;
    const primaryButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (shouldFocusOnToastButton) {
            trySetFocusOnElement(primaryButtonRef);
        } else {
            // If we are focusing on the toast button, we do not need to add the aria label as focus would do the job.
            addAriaLiveLabel(item.label || '', 'assertive');
        }

        return () => {
            !shouldFocusOnToastButton && removeAriaLiveLabel(item.label); // remove the added aria label
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
         * See aka.ms/mini-lint-errors
         *	> React Hook React.useEffect has missing dependencies: 'item.label' and 'shouldFocusOnToastButton'. Either include them or remove the dependency array. */
    }, []);

    // Show the toast only when a valid object is sent and only when view state has its value true
    if (toastViewState.isToastShown && item) {
        const {
            className,
            onPrimaryButtonClick,
            icon,
            label,
            primaryButtonLabel,
            numberOfRetriesAllowed,
            secondaryButtonLabel,
            onSecondaryButtonClick,
        } = item;
        const buttonList = [
            {
                onClick: onPrimaryButtonClick,
                label: primaryButtonLabel,
                ref: primaryButtonRef,
                ariaLabel: `${label} ${primaryButtonLabel}`,
            },
            { onClick: onSecondaryButtonClick, label: secondaryButtonLabel },
        ];

        return (
            <div
                className={classNames(
                    styles.toast,
                    { [styles.toastMultiButton]: !!secondaryButtonLabel },
                    className
                )}>
                <div className={classNames(styles.textAndIconContainer)}>
                    {icon && (
                        <FluentSvgIcon icon={icon} iconContainerStyles={styles.icon} aria-hidden />
                    )}
                    <div className={styles.toastLabel} aria-hidden>
                        {label}
                    </div>
                </div>
                <div className={classNames(styles.buttonContainer)}>
                    {buttonList.map(({ onClick, label, ref = null, ariaLabel = '' }) =>
                        label ? (
                            <button
                                key={label}
                                className={classNames(styles.actionButton)}
                                onClick={evt =>
                                    onToastClicked(evt, numberOfRetriesAllowed, onClick, onRetry)
                                }
                                tabIndex={0}
                                ref={ref}
                                aria-label={ariaLabel}>
                                {label}
                            </button>
                        ) : null
                    )}
                </div>
            </div>
        );
    }

    return null;
};

/**
 * Component which shows the toast
 * @param props
 */
export default observer(Toast);
